import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';

import {
  BpkButtonV2,
  SIZE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import BpkLink from '@skyscanner/backpack-web/bpk-component-link';
import BpkModal from '@skyscanner/backpack-web/bpk-component-modal';
import BpkPrice, { SIZES } from '@skyscanner/backpack-web/bpk-component-price';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text/src/BpkText';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import SemiModal from '../../common/SemiModal/SemiModal';
import { withI18n } from '../../contexts/i18n';
import { withMiniEventTracker } from '../../contexts/mini-events';

import CostItems from './CostItems/CostItems';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { PriceBreakdownInfo } from '@shared/types/frontend/price-breakdown';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Nullable } from '@shared/types/utils';

import STYLES from './TotalPriceBreakdown.module.scss';

import { getApplicationElement } from '@shared/utils-frontend/ApplicationElements';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import {
  ACTION_TYPE,
  PRICE_TYPE,
} from '@shared/utils-frontend/mini-events/constants';
import {
  buildAdditionalInfoPriceBreakdownSelected,
  buildAdditionalInfoViewButtonClicked,
} from '@shared/utils-frontend/mini-events/hotels-action';
import { isTaxesFeesIncludedPolicy } from '@shared/utils-frontend/price-policy';
import {
  getButtonCopyKey,
  getSubPriceLabel,
  getPriceTypeLabel,
  enableSubPrice,
} from '@shared/utils-frontend/total-price-breakdown-service';

export const ALIGNS = {
  left: 'left' as const,
  right: 'right' as const,
};

type Props = {
  i18n: I18nService;
  pricePolicy?: Nullable<string>;
  isMobile: boolean;
  costInfo: PriceBreakdownInfo;
  deeplinkUrl?: string;
  pageType?: string;
  miniEventTracker: MiniEventTracker;
  leadingText?: Nullable<ReactNode>;
  previousPriceNode?: Nullable<string>;
  userContext: UserContext;
  onTrackPriceSelected?: Function;
  showYourPick?: boolean;
  clickArea: string;
  large?: boolean;
  align: (typeof ALIGNS)[keyof typeof ALIGNS];
};

const cls = cssModules(STYLES);

const TotalPriceBreakdown = ({
  align,
  clickArea,
  costInfo,
  deeplinkUrl,
  i18n,
  isMobile,
  large,
  leadingText,
  miniEventTracker,
  onTrackPriceSelected,
  previousPriceNode,
  showYourPick,
  userContext: { totalCostStyleVar },
}: Props) => {
  const { formatCurrency, translate } = i18n;
  const [isOpen, setIsOpen] = useState(false);
  const {
    isPerNightType,
    numberOfNights,
    priceInfo: { price, priceWithAllTaxes, secondaryPrice },
    taxAndFeeInclusionPolicy,
  } = costInfo;
  const taxesIncluded = isTaxesFeesIncludedPolicy(taxAndFeeInclusionPolicy);
  const shouldShowSubPrice =
    (!isMobile || enableSubPrice(totalCostStyleVar)) &&
    numberOfNights > 1 &&
    taxesIncluded;
  const shouldShowTotalWithTaxesPrice = !taxesIncluded && !isPerNightType;
  const priceTypeLabel = getPriceTypeLabel({
    taxesIncluded,
    isMobile,
    isPerNightType,
    i18n,
    shouldShowTotalWithTaxesPrice,
  });
  const mainPrice = shouldShowTotalWithTaxesPrice ? priceWithAllTaxes : price;

  const subPriceLabel = getSubPriceLabel({
    isPerNightType,
    price: secondaryPrice,
    i18n,
  });

  const onToggleModal = (openState: boolean, event?: MouseEvent) => {
    if (openState && event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setIsOpen(openState);
  };

  const openPriceBreakdown = (event: MouseEvent) => {
    onToggleModal(true, event);
    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.VIEW_BUTTON_CLICKED,
      buildAdditionalInfoViewButtonClicked(clickArea),
    );
  };

  const onSelectButtonClick = (event: any) => {
    onToggleModal(false, event);
    !!deeplinkUrl &&
      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.PRICE_BREAKDOWN_SELECTED,
        buildAdditionalInfoPriceBreakdownSelected({
          priceType: PRICE_TYPE.META,
        }),
      );
    onTrackPriceSelected?.();
  };

  const modalProps = {
    id: 'room-rates-price-breadown-modal',
    isOpen,
    onClose: () => onToggleModal(false),
    title: translate('Price_breakdown_title'),
  };

  const footerButton = (
    <BpkButtonV2
      size={SIZE_TYPES.large}
      className={cls('TotalPriceBreakdown__closeButton')}
      onClick={onSelectButtonClick}
      href={deeplinkUrl}
      target="_blank"
    >
      {translate(getButtonCopyKey(!!deeplinkUrl))}
    </BpkButtonV2>
  );

  return (
    <div
      className={cls(
        'TotalPriceBreakdown',
        align === ALIGNS.left && 'TotalPriceBreakdown--left',
      )}
    >
      <BpkPrice
        size={large ? SIZES.large : SIZES.small}
        align={align}
        price={
          <BpkLink
            as="button"
            implicit={shouldShowSubPrice}
            onClick={shouldShowSubPrice ? undefined : openPriceBreakdown}
            className={cls(
              shouldShowSubPrice ? '' : 'TotalPriceBreakdown__mainPriceLink',
            )}
            tabIndex={shouldShowSubPrice ? -1 : 0}
          >
            {formatCurrency(mainPrice)}
          </BpkLink>
        }
        previousPrice={previousPriceNode}
        leadingText={leadingText}
        trailingText={priceTypeLabel}
        dataAttributes={{
          'data-price-your-pick': showYourPick ? 'true' : undefined,
        }}
      />
      {shouldShowSubPrice && (
        <BpkLink
          as="button"
          onClick={openPriceBreakdown}
          className={cls('TotalPriceBreakdown__subPriceLink')}
        >
          <BpkText textStyle={large ? TEXT_STYLES.sm : TEXT_STYLES.caption}>
            {subPriceLabel}
          </BpkText>
        </BpkLink>
      )}
      {isMobile ? (
        <SemiModal {...modalProps} footer={footerButton}>
          <CostItems costInfo={costInfo} numberOfNights={numberOfNights} />
        </SemiModal>
      ) : (
        <BpkModal
          {...modalProps}
          padded={false}
          getApplicationElement={getApplicationElement}
        >
          <div className={cls('TotalPriceBreakdown__modalContent')}>
            <CostItems costInfo={costInfo} numberOfNights={numberOfNights} />
            {footerButton}
          </div>
        </BpkModal>
      )}
    </div>
  );
};

export default withUserContext(
  withMiniEventTracker(withI18n(TotalPriceBreakdown)),
);
